import type { TenantConfig, TenantName, TenantPdfConfig } from '../@types/types';

import { pdfTenantsWatermarkConfig } from './tenant.pdf-watermark';

const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1);

const client: TenantName = import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT;
if (!client) {
  throw new Error('Missing env var: CLIENT');
}

const appName: string = import.meta.env?.VITE_APP_NAME ?? process?.env?.APP_NAME;
if (!appName) {
  throw new Error('Missing env var: APP_NAME');
}

export const pdfTenantsConfig = (client: TenantName): TenantPdfConfig => {
  const defaultPdfConfig: TenantPdfConfig = {
    format: 'A4',
    documents: {
      templates: ['template-document'],
      submissions: ['submission-document'],
      endorsements: ['endorsement-summary'],
      brandings: ['branding-document'],
    },
  };

  switch (client) {
    case 'ct-ppl':
      return defaultPdfConfig;
    default:
      return {
        ...defaultPdfConfig,
        watermark: pdfTenantsWatermarkConfig({ client }),
      };
  }
};

const artificialConfig = (): TenantConfig => {
  return {
    name: 'Contract builder',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    headerLogoClasses: 'h-12',
    pdf: pdfTenantsConfig('artificial'),
    isAttachmentsEnabled: true,
    isDataExtractionEnabled: true,
    isEndorsementEnabled: true,
    isRevisionsEnabled: true,
    isSSOEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctAonConfig = (): TenantConfig => {
  return {
    name: 'Contract builder',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    headerLogoClasses: 'h-12',
    pdf: pdfTenantsConfig('artificial'),
    isAttachmentsEnabled: true,
    isDataExtractionEnabled: false,
    isEndorsementEnabled: true,
    isRevisionsEnabled: true,
    isSSOEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctLockton = (): TenantConfig => {
  return {
    name: 'Lockton',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-lockton'),
    isAttachmentsEnabled: true,
    isDataExtractionEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    isTemplateReviewWorkflowEnabled: false,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctBms = (): TenantConfig => {
  return {
    name: 'BMS',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-bms'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    isTemplateReviewWorkflowEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctAjg = (): TenantConfig => {
  return {
    name: 'AJG',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-ajg'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isDataExtractionEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctHowden = (): TenantConfig => {
  return {
    name: 'Howden',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-howden'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isDataExtractionEnabled: false,
    isSSOEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctMgcill = (): TenantConfig => {
  return {
    name: 'Mcgill',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-mcgill'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isDataExtractionEnabled: true,
    isSSOEnabled: true,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const ctPpl = (): TenantConfig => {
  return {
    name: 'PPL',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-ppl'),
    isAttachmentsEnabled: false,
    isBrandingEnabled: true,
    isDataExtractionEnabled: false,
    isEndorsementEnabled: false,
    isTempContractEnabled: false,
    isSwapBaseTemplateEnabled: false,
    isSaveCloseEnabled: false,
    cognito: {
      linkedLogo: 'https://assets.artificial.io/mailing/artificial-full.png',
      emailSender: 'noreply@mail.contract-builder.artificialos.com',
      appName,
      appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
      client: capitalize(client),
      footerAddress: '',
    },
    teams: [],
  };
};

const getTenantConfig = (): TenantConfig => {
  if (client === 'artificial') {
    return artificialConfig();
  }

  if (client === 'smart-placement') {
    return artificialConfig();
  }

  if (client === 'ct-aon') {
    return ctAonConfig();
  }

  if (client === 'ct-lockton') {
    return ctLockton();
  }

  if (client === 'ct-bms') {
    return ctBms();
  }

  if (client === 'ct-ajg') {
    return ctAjg();
  }

  if (client === 'ct-howden') {
    return ctHowden();
  }

  if (client === 'ct-mcgill') {
    return ctMgcill();
  }

  if (client === 'ct-ppl') {
    return ctPpl();
  }

  return artificialConfig();
};

export const tenantConfig: TenantConfig = getTenantConfig();
